import React, { useEffect, useMemo, useState } from 'react';
import { FaUsers, FaPlus, FaTimes, FaEdit } from 'react-icons/fa';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

type TenantClient = { id: string; name: string; code: string };
type TenantFacility = { id: string; name: string; code: string; clientId: string };
type GroupOption = { id: number; name: string };

type UserRow = {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  is_active?: boolean;
  groups?: string[];
  is_superuser?: boolean;
  client?: string;
  client_id?: string;
  facility?: string;
  facility_id?: string;
  role?: string;
  license?: string;
  renewal_date?: string;
  license_renewal_date?: string;
};

const EMPTY_FORM = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  confirm_password: '',
  is_active: true,
  client_code: '',
  group_ids: [] as number[],
  facility_scope: 'all' as 'all' | 'assigned',
  assigned_facility_codes: [] as string[],
};

const resolveRole = (user: UserRow) => {
  if (user.is_superuser || user.username === 'admin') return 'Global Admin';
  if (user.role) return user.role;
  if (Array.isArray(user.groups) && user.groups.length > 0) return user.groups.join(', ');
  return 'User';
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-AU');
};

const normalize = (value?: string) => (value || '').trim().toLowerCase();

const ClientUsersAdmin: React.FC = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [users, setUsers] = useState<UserRow[]>([]);
  const [clients, setClients] = useState<TenantClient[]>([]);
  const [facilities, setFacilities] = useState<TenantFacility[]>([]);
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editUser, setEditUser] = useState<UserRow | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [usersRes, tenantRes] = await Promise.all([
        apiCall('/authentication/users/', { method: 'GET' }, token),
        apiCall('/core/tenant-context/', { method: 'GET' }, token),
      ]);

      if (!usersRes.ok) throw new Error(`Failed to load users: HTTP ${usersRes.status}`);
      const usersData = await usersRes.json();
      setUsers(Array.isArray(usersData) ? usersData : usersData.results || []);

      if (tenantRes.ok) {
        const tenantData = await tenantRes.json();
        const tenantClients = (tenantData.clients || []).map((c: any) => ({
          id: String(c.id || ''),
          name: String(c.name || c.id || ''),
          // tenant-context returns id as lowercase code (e.g. 'metropain')
          // We store uppercase for the API payload
          code: String(c.code || c.id || '').toUpperCase(),
        }));
        setClients(tenantClients);

        const tenantFacilities = (tenantData.facilities || []).map((f: any) => ({
          id: String(f.id || ''),
          name: String(f.name || ''),
          code: String(f.code || '').toUpperCase(),
          clientId: String(f.clientId || '').toLowerCase(),
        }));
        setFacilities(tenantFacilities);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async (clientCode: string) => {
    if (!token || !clientCode) { setGroups([]); return; }
    try {
      const res = await apiCall(
        `/authentication/groups/?client_id=${clientCode.toLowerCase()}`,
        { method: 'GET' }, token
      );
      if (res.ok) {
        const data = await res.json();
        setGroups(Array.isArray(data) ? data : data.results || []);
      }
    } catch { setGroups([]); }
  };

  useEffect(() => { loadData(); }, [token]);

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((c) => map.set(normalize(c.id), c.name));
    return map;
  }, [clients]);

  const filteredUsers = useMemo(() => {
    const sel = normalize(selectedClient);
    return users
      .filter((u) => sel === 'all' || normalize(u.client_id || u.client) === sel)
      .sort((a, b) => {
        if (a.username === 'admin') return -1;
        if (b.username === 'admin') return 1;
        return a.username.localeCompare(b.username);
      });
  }, [users, selectedClient]);

  const facilitiesForClient = useMemo(() => {
    const code = form.client_code.toLowerCase();
    if (!code) return [];
    return facilities.filter((f) => f.clientId === code);
  }, [facilities, form.client_code]);

  const openCreate = () => {
    setEditUser(null);
    // Pre-select the currently filtered client if one is selected
    const defaultClientId = selectedClient !== 'all' ? selectedClient : '';
    const defaultClientCode = defaultClientId.toUpperCase();
    setForm({ ...EMPTY_FORM, client_code: defaultClientCode });
    if (defaultClientId) loadGroups(defaultClientId);
    setSaveError('');
    setModalOpen(true);
  };

  const openEdit = (user: UserRow) => {
    setEditUser(user);
    setForm({
      ...EMPTY_FORM,
      username: user.username || '',
      email: user.email || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      is_active: user.is_active !== false,
      client_code: (user.client_id || user.client || '').toUpperCase(),
    });
    setSaveError('');
    setModalOpen(true);
  };

  const handleClientChange = (code: string) => {
    // code here is the lowercase client id from tenant-context (e.g. 'metropain')
    // store uppercase for the API payload, lowercase for facility filtering
    setForm(f => ({ ...f, client_code: code.toUpperCase(), group_ids: [], assigned_facility_codes: [] }));
    loadGroups(code);
  };

  const toggleFacility = (code: string) => {
    setForm(f => ({
      ...f,
      assigned_facility_codes: f.assigned_facility_codes.includes(code)
        ? f.assigned_facility_codes.filter(c => c !== code)
        : [...f.assigned_facility_codes, code],
    }));
  };

  const toggleGroup = (id: number) => {
    setForm(f => ({
      ...f,
      group_ids: f.group_ids.includes(id)
        ? f.group_ids.filter(g => g !== id)
        : [...f.group_ids, id],
    }));
  };

  const handleSave = async () => {
    setSaveError('');
    if (!form.username || !form.email) { setSaveError('Username and email are required.'); return; }
    if (!editUser) {
      if (!form.password) { setSaveError('Password is required.'); return; }
      if (form.password !== form.confirm_password) { setSaveError('Passwords do not match.'); return; }
    }

    setSaving(true);
    try {
      if (editUser) {
        const res = await apiCall(`/authentication/users/${editUser.id}/`, {
          method: 'PATCH',
          body: JSON.stringify({
            first_name: form.first_name,
            last_name: form.last_name,
            email: form.email,
            is_active: form.is_active,
          }),
        }, token!);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || JSON.stringify(err));
        }
      } else {
        const res = await apiCall('/authentication/users/', {
          method: 'POST',
          body: JSON.stringify({
            username: form.username,
            email: form.email,
            first_name: form.first_name,
            last_name: form.last_name,
            password: form.password,
            is_active: form.is_active,
            groups: form.group_ids,
            client_code: form.client_code,
            facility_scope: form.facility_scope,
            assigned_facility_codes: form.facility_scope === 'assigned' ? form.assigned_facility_codes : [],
          }),
        }, token!);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || JSON.stringify(err));
        }
      }
      setModalOpen(false);
      await loadData();
    } catch (e: any) {
      setSaveError(e.message || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-base-content flex items-center gap-3">
          <FaUsers className="text-primary" />
          Client Users
        </h1>
        <button className="btn btn-primary" onClick={openCreate}>
          <FaPlus className="mr-2" /> Add User
        </button>
      </div>

      <div className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <label className="form-control w-full md:w-80">
              <span className="label"><span className="label-text font-semibold">Client</span></span>
              <select
                className="select select-bordered"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
              >
                <option value="all">All</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
          </div>

          {error && <div className="alert alert-error mt-4"><span>{error}</span></div>}

          <div className="overflow-x-auto mt-4">
            <table className="table table-zebra table-fixed w-full">
              <thead>
                <tr>
                  <th className="w-[14%]">Username</th>
                  <th className="w-[12%]">First Name</th>
                  <th className="w-[12%]">Last Name</th>
                  <th className="w-[16%]">Client</th>
                  <th className="w-[16%]">Facility</th>
                  <th className="w-[14%]">Role</th>
                  <th className="w-[8%]">License</th>
                  <th className="w-[8%]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="text-center">Loading users...</td></tr>
                ) : filteredUsers.length === 0 ? (
                  <tr><td colSpan={8} className="text-center">No users found for selected client.</td></tr>
                ) : filteredUsers.map((user) => {
                  const immutable = user.username === 'admin';
                  const clientKey = normalize(user.client_id || user.client);
                  const resolvedClient = clientNameById.get(clientKey) || user.client || user.client_id || (immutable ? 'Global' : '-');
                  const facility = user.facility || user.facility_id || (immutable ? 'All Facilities' : '-');
                  const license = user.license || (immutable ? 'N/A' : '-');
                  return (
                    <tr key={user.id || user.username}>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{user.username}</span>
                          {immutable && <span className="badge badge-neutral badge-sm">Immutable</span>}
                        </div>
                      </td>
                      <td>{user.first_name || '-'}</td>
                      <td>{user.last_name || '-'}</td>
                      <td>{resolvedClient}</td>
                      <td>{facility}</td>
                      <td>{resolveRole(user)}</td>
                      <td>{license}</td>
                      <td>
                        {!immutable && (
                          <button className="btn btn-ghost btn-xs" onClick={() => openEdit(user)} title="Edit">
                            <FaEdit />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit User Modal */}
      {modalOpen && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl max-h-[90vh] flex flex-col p-0">
            <div className="sticky top-0 bg-base-100 z-10 px-6 py-4 border-b border-base-300 flex justify-between items-center">
              <h3 className="text-xl font-bold">{editUser ? 'Edit User' : 'Add New User'}</h3>
              <button className="btn btn-ghost btn-sm btn-circle" onClick={() => setModalOpen(false)}>
                <FaTimes />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {saveError && <div className="alert alert-error"><span>{saveError}</span></div>}

              <div className="grid grid-cols-2 gap-4">
                <div className="form-control">
                  <label className="label"><span className="label-text font-semibold">Username *</span></label>
                  <input
                    type="text" className="input input-bordered"
                    value={form.username}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    disabled={!!editUser}
                  />
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text font-semibold">Email *</span></label>
                  <input
                    type="email" className="input input-bordered"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text font-semibold">First Name</span></label>
                  <input
                    type="text" className="input input-bordered"
                    value={form.first_name}
                    onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))}
                  />
                </div>
                <div className="form-control">
                  <label className="label"><span className="label-text font-semibold">Last Name</span></label>
                  <input
                    type="text" className="input input-bordered"
                    value={form.last_name}
                    onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))}
                  />
                </div>
              </div>

              {!editUser && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-control">
                    <label className="label"><span className="label-text font-semibold">Password *</span></label>
                    <input
                      type="password" className="input input-bordered"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text font-semibold">Confirm Password *</span></label>
                    <input
                      type="password" className="input input-bordered"
                      value={form.confirm_password}
                      onChange={e => setForm(f => ({ ...f, confirm_password: e.target.value }))}
                    />
                  </div>
                </div>
              )}

              <label className="label cursor-pointer justify-start gap-4">
                <input
                  type="checkbox" className="checkbox checkbox-primary"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                />
                <span className="label-text font-semibold">Active User</span>
              </label>

              {!editUser && (
                <>
                  <div className="form-control">
                    <label className="label"><span className="label-text font-semibold">Client</span></label>
                    <select
                      className="select select-bordered"
                      value={form.client_code}
                      onChange={e => handleClientChange(e.target.value)}
                    >
                      <option value="">— Select client —</option>
                      {clients.map(c => (
                        <option key={c.code} value={c.code}>{c.name}</option>
                      ))}
                    </select>
                  </div>

                  {form.client_code && (
                    <>
                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Role / Group</span></label>
                        <div className="border border-base-300 rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                          {groups.length === 0
                            ? <p className="text-sm text-base-content/60">No groups found for this client</p>
                            : groups.map(g => (
                              <label key={g.id} className="label cursor-pointer justify-start gap-3">
                                <input
                                  type="checkbox" className="checkbox checkbox-primary checkbox-sm"
                                  checked={form.group_ids.includes(g.id)}
                                  onChange={() => toggleGroup(g.id)}
                                />
                                <span className="label-text">{g.name.includes('::') ? g.name.split('::')[1] : g.name}</span>
                              </label>
                            ))
                          }
                        </div>
                      </div>

                      <div className="form-control">
                        <label className="label"><span className="label-text font-semibold">Facility Access</span></label>
                        <div className="flex gap-4 mb-2">
                          <label className="label cursor-pointer gap-2">
                            <input
                              type="radio" className="radio radio-primary radio-sm"
                              checked={form.facility_scope === 'all'}
                              onChange={() => setForm(f => ({ ...f, facility_scope: 'all', assigned_facility_codes: [] }))}
                            />
                            <span className="label-text">All facilities</span>
                          </label>
                          <label className="label cursor-pointer gap-2">
                            <input
                              type="radio" className="radio radio-primary radio-sm"
                              checked={form.facility_scope === 'assigned'}
                              onChange={() => setForm(f => ({ ...f, facility_scope: 'assigned' }))}
                            />
                            <span className="label-text">Specific facilities</span>
                          </label>
                        </div>
                        {form.facility_scope === 'assigned' && (
                          <div className="border border-base-300 rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                            {facilitiesForClient.length === 0
                              ? <p className="text-sm text-base-content/60">No facilities available</p>
                              : facilitiesForClient.map(f => (
                                <label key={f.code} className="label cursor-pointer justify-start gap-3">
                                  <input
                                    type="checkbox" className="checkbox checkbox-primary checkbox-sm"
                                    checked={form.assigned_facility_codes.includes(f.code)}
                                    onChange={() => toggleFacility(f.code)}
                                  />
                                  <span className="label-text">{f.name}</span>
                                </label>
                              ))
                            }
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="sticky bottom-0 bg-base-100 px-6 py-4 border-t border-base-300 flex justify-end gap-2">
              <button className="btn btn-ghost" onClick={() => setModalOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? <span className="loading loading-spinner loading-sm" /> : null}
                {editUser ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientUsersAdmin;
